import React from 'react';
import { observer } from 'mobx-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CommonModal } from '../../../../../../../global/modal/common/common-modal';
import DefaultButton from '../../../../../../../global/default-button/default-button';
import Spinner from '../../../../../../../global/spinner/spinner';
import UploadButton, { FILE_TYPE } from '../../../../../../../_events/_manage-event/shared/upload-button';
import { ADD_TEXT, APPLY_TEXT, CANCEL_TEXT, REQUIRED_FIELD_TEXT, } from '../../../../../../../../constants/common-labels.constants';
import { EnablementStatus } from '../../../../../../../../constants/common.constants';
import {
    EDIT_CUSTOM_HTML_MODULE_MODAL_TITLE_TEXT,
    MODULE_ACTIVE_LABEL_TEXT,
    MODULE_CUSTOM_HTML_IFRAME_HEIGHT_LABEL_TEXT,
    MODULE_CUSTOM_HTML_IFRAME_HEIGHT_SUFFIX_TEXT,
    MODULE_CUSTOM_HTML_ZIP_FIELD_LABEL_TEXT,
    MODULE_CUSTOM_HTML_ZIP_HINT_TEXT,
    MODULE_LABEL_DISPLAYED_LABEL_TEXT,
    MODULE_LABEL_FIELD_LABEL_TEXT,
    NEW_CUSTOM_HTML_MODULE_MODAL_TITLE_TEXT,
} from '../../../../../../event-hubs.text';
import { CustomHtmlModuleVM } from './custom-html-module.viewmodel';
import { IFRAME_HEIGHT, LABEL, ZIP_FILE } from './custom-html-module.config';
import { FormField } from '../../../../../../../global/form/form-field/form-field';
import { FormFieldInput } from '../../../../../../../global/form/form-field-input/form-field-input';
import { FormFieldError } from '../../../../../../../global/form/form-field-input/form-field-error';
import './custom-html-module-modal.scss';

type CustomHtmlModuleModalProps = {
    vm: CustomHtmlModuleVM;
    isNew: boolean;
    onCancel: () => void;
    onConfirm: () => void;
};

export const CustomHtmlModuleModal: React.FC<CustomHtmlModuleModalProps> = observer(props => {
    const { vm, isNew, onCancel, onConfirm } = props;
    const { formatMessage } = useIntl();

    const zipField = vm.getFormVmValuePropsByKey(ZIP_FILE);
    const heightField = vm.getFormVmValuePropsByKey(IFRAME_HEIGHT);
    const fileName = vm.getFileName();

    const titleDescriptor = isNew ? NEW_CUSTOM_HTML_MODULE_MODAL_TITLE_TEXT : EDIT_CUSTOM_HTML_MODULE_MODAL_TITLE_TEXT;
    const confirmDescriptor = isNew ? ADD_TEXT : APPLY_TEXT;

    const handleActiveToggle = () => vm.setEnabled(!vm.isEnabled());
    // the label is never displayed for this module: the toggle is disabled and stays off, the handler is only required by the component
    const handleLabelDisplayedToggle = (): void => undefined;
    const handleFileSelect = (file: File | File[]) => vm.selectFile(file as File);
    const handleHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => heightField.setValue(e.target.value);

    const hasLocalFileError = !zipField.valid && zipField.touched;

    return (
        <CommonModal
            isOpen
            title={<FormattedMessage {...titleDescriptor} />}
            onRequestClose={onCancel}
            className='custom-html-module-modal'
            footer={
                <div className='custom-html-module-modal__footer'>
                    <DefaultButton
                        modifier='cancel'
                        text={<FormattedMessage {...CANCEL_TEXT} />}
                        onClick={onCancel}
                    />
                    <DefaultButton
                        text={<FormattedMessage {...confirmDescriptor} />}
                        onClick={onConfirm}
                        disabled={vm.isUploading}
                    />
                </div>
            }
        >
            <div className='custom-html-module-modal__body'>
                <FormFieldInput.Toggle
                    isSelected={vm.isEnabled()}
                    toggle={handleActiveToggle}
                    text={<FormattedMessage
                        values={{ suffix: vm.isEnabled() ? EnablementStatus.ENABLED : EnablementStatus.DISABLED }}
                        {...MODULE_ACTIVE_LABEL_TEXT}
                    />}
                    className='custom-html-module-modal__toggle-row'
                />

                <div className='custom-html-module-modal__label-row'>
                    <FormFieldInput.Text
                        field={vm.getFormVmValuePropsByKey(LABEL)}
                        labelDescriptor={MODULE_LABEL_FIELD_LABEL_TEXT}
                        required
                        additionalClassName='custom-html-module-modal__label-input'
                        withError
                    />

                    <FormFieldInput.Toggle
                        isSelected={false}
                        toggle={handleLabelDisplayedToggle}
                        isDisabled
                        text={<FormattedMessage
                            values={{ suffix: EnablementStatus.NOT_DISPLAYED }}
                            {...MODULE_LABEL_DISPLAYED_LABEL_TEXT}
                        />}
                        className='custom-html-module-modal__toggle-row custom-html-module-modal__toggle-row--inline'
                    />
                </div>

                <div className='oe-form-field m-bottom-20px'>
                    <div className='oe-form-field__title flex'>
                        <FormattedMessage {...MODULE_CUSTOM_HTML_ZIP_FIELD_LABEL_TEXT} />
                        <span className='oe-form-field__title-required'><FormattedMessage {...REQUIRED_FIELD_TEXT} /></span>
                    </div>
                    {vm.isUploading ?
                        <Spinner /> :
                        <UploadButton
                            fileName={fileName}
                            onChange={handleFileSelect}
                            uploadedFileType={FILE_TYPE.ZIP}
                            additionalText={<FormattedMessage {...MODULE_CUSTOM_HTML_ZIP_HINT_TEXT} />}
                        />
                    }
                    <span className='oe-form-field__error'>
                        {vm.fileError && <FormattedMessage values={vm.fileError.values} {...vm.fileError.descriptor} />}
                        {!vm.fileError && hasLocalFileError &&
                            <FormattedMessage
                                values={{ fieldName: formatMessage(MODULE_CUSTOM_HTML_ZIP_FIELD_LABEL_TEXT) }}
                                {...zipField.validationMessage}
                            />
                        }
                    </span>
                </div>

                <FormField
                    className='m-bottom-20px custom-html-module-modal__height-field'
                    label={<>
                        <FormattedMessage {...MODULE_CUSTOM_HTML_IFRAME_HEIGHT_LABEL_TEXT} />
                        <span className='oe-form-field__title-required'><FormattedMessage {...REQUIRED_FIELD_TEXT} /></span>
                    </>}
                >
                    <div className='custom-html-module-modal__height-control'>
                        <input
                            className='oe-form-field__control'
                            type='number'
                            value={(heightField.value ?? '') as string | number}
                            onChange={handleHeightChange}
                            onBlur={heightField.setTouched}
                        />
                        <span className='custom-html-module-modal__height-suffix'>
                            <FormattedMessage {...MODULE_CUSTOM_HTML_IFRAME_HEIGHT_SUFFIX_TEXT} />
                        </span>
                    </div>
                    <FormFieldError field={heightField} labelDescriptor={MODULE_CUSTOM_HTML_IFRAME_HEIGHT_LABEL_TEXT} />
                </FormField>
            </div>
        </CommonModal>
    );
});
